import { FC, useState, useEffect, useMemo, useCallback } from 'react'
import Select from 'react-select'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { KTIcon } from '../../../../_metronic/helpers'
import { fetchData, toasterrormsg, toastsuccessmsg } from '../../../../reusable'
import { SelectOption, SponserStepCommonProps } from '../../types/sponser'

interface Props extends SponserStepCommonProps {
  vendorList: SelectOption[]
  goToNext: () => void
}

const propertySelectionSchema = Yup.object().shape({
  vendorId: Yup.string().required('Please Select A Vendor'),
})

// Categories where we select the main property directly (no sub-items)
const DIRECT_CATEGORIES = [1, 2, 3, 4]
// Categories where we select a parent, then sub-items
const NESTED_CATEGORIES = [5, 7]

// actionType sent to the API, keyed by the vendor's categoryId.
const ACTION_TYPE_BY_CATEGORY: Record<number, string> = {
  1: 'hotel',
  3: 'restaurant',
  4: 'space',
  5: 'tours',
  7: 'vehicle',
}

const CATEGORY_TYPES = [
  { value: 1, detailApi: 'vendor/public/hotelDetailsV2', subKey: 'roomList', parentIdKey: 'hotelId', subIdKey: 'roomId', nameKey: 'en_roomName' },
  { value: 4, detailApi: 'vendor/public/spaceDetails', subKey: 'rentablespace', parentIdKey: 'spacerentalId', subIdKey: 'rentablespaceId', nameKey: 'en_name' },
  // tourpackageDetails takes the parent tour package as `tourpackageId`; its toursDetail[] items are keyed by `toursId`.
  { value: 5, detailApi: 'vendor/public/tourpackageDetails', subKey: 'toursDetail', parentIdKey: 'tourpackageId', subIdKey: 'toursId', nameKey: 'en_name' },
  { value: 7, detailApi: 'vendor/public/carRentalDetail', subKey: 'vehcileDetail', parentIdKey: 'carrentalId', subIdKey: 'vehicleId', nameKey: 'en_name' },
]

const PropertySelectionStep: FC<Props> = ({ t, sponserId, adminId, sponserData, vendorList, onSaveSuccess, goToNext }) => {
  const safeVendors = useMemo(() => Array.isArray(vendorList) ? vendorList : [], [vendorList])

  const [selectedVendor, setSelectedVendor] = useState<SelectOption | null>(null)
  const [saving, setSaving] = useState(false)

  // Sponsored property states
  const [propertyList, setPropertyList] = useState<SelectOption[]>([])
  const [propertyLoading, setPropertyLoading] = useState(false)
  const [selectedProperty, setSelectedProperty] = useState<SelectOption | null>(null)

  // For nested categories — sub-items
  const [subItemList, setSubItemList] = useState<SelectOption[]>([])
  const [subItemLoading, setSubItemLoading] = useState(false)
  const [selectedSubItems, setSelectedSubItems] = useState<(string | number)[]>([])
  const [noSubItems, setNoSubItems] = useState(false)
  const [propertyError, setPropertyError] = useState('')

  const categoryId = useMemo(() => Number(sponserData?.categoryId) || 0, [sponserData?.categoryId])  
  
  const isDirect = useMemo(() => DIRECT_CATEGORIES.includes(categoryId), [categoryId])
  const isNested = useMemo(() => NESTED_CATEGORIES.includes(categoryId), [categoryId])
  const catConfig = useMemo(() => CATEGORY_TYPES.find((c) => c.value === categoryId), [categoryId])

  const formik = useFormik({
    initialValues: {
      vendorId: '',
    },
    validationSchema: propertySelectionSchema,
    onSubmit: async (values) => {
      // Validate property selection
      if (!selectedProperty?.value) {
        setPropertyError(t('Please select a property'))
        return
      }
      if (isNested && selectedSubItems.length === 0 && subItemList.length > 0) {
        setPropertyError(t('Please select at least one item'))
        return
      }
      setPropertyError('')

      setSaving(true)
      try {
        let actionTypeId: string | string[] = ''
        if (isNested && selectedSubItems.length > 0) {
          actionTypeId = selectedSubItems.length === 1 ? String(selectedSubItems[0]) : selectedSubItems.map((id) => String(id))
        } else if (selectedProperty?.value) {
          actionTypeId = String(selectedProperty.value)
        }

        const completeSteps = { ...(sponserData?.completeSteps ?? {}), action: true }
        const param = {
          adminId,
          // vendorId: values.vendorId,
          sponserId: sponserId || 0,
          // Derived from the category: the API returns null until this step saves, so
          // echoing it back would keep it empty forever.
          actionType: ACTION_TYPE_BY_CATEGORY[categoryId] ?? sponserData?.actionType ?? '',
          actionTypeId,
          completeSteps,
        }
        const response = await fetchData('admin/sponser/action/save', param)
        if (String(response?.status) === '200') {
          toastsuccessmsg(response?.message || 'Property Selection Saved')
          onSaveSuccess(response?.data ?? {})
          goToNext()
        } else {
          toasterrormsg(response?.message || 'Failed to save property selection')
        }
      } catch (error) {
        console.error('Property selection save error:', error)
        toasterrormsg('Failed to save property selection')
      } finally {
        setSaving(false)
      }
    },
  })

  // Pre-fill vendor
  useEffect(() => {
    if (sponserData?.vendorId) formik.setFieldValue('vendorId', String(sponserData.vendorId))
    if (sponserData?.vendorId && safeVendors.length > 0) {
      const found = safeVendors.find((v) => String(v.value) === String(sponserData.vendorId))
      if (found) setSelectedVendor(found)
    }
  }, [sponserData, safeVendors.length])

  // Fetch properties when vendor + category are known
  useEffect(() => {
    if (!sponserData?.vendorId || !categoryId) return
    fetchProperties(categoryId, sponserData.vendorId)
  }, [sponserData?.vendorId, categoryId])

  // Pre-fill selected items from sponserData
  useEffect(() => {
    if (!sponserData) return

    if (isDirect) {
      // Direct: actionTypeId is the main property, en_name is the label
      const rawId = sponserData.actionTypeId
      const id = Array.isArray(rawId) ? rawId[0] : rawId
      if (id) {
        const savedName = sponserData?.actionDetail?.[0]?.en_name || sponserData?.en_name || ''
        setSelectedProperty({ value: id, label: savedName || String(id) })
      }
    }

    if (isNested) {
      // Nested: parentId is the main parent, actionTypeId is the sub-item
      const parentId = sponserData?.parentId ?? ''
      const parentName = sponserData?.en_parentName ?? ''
      if (parentId) {
        setSelectedProperty({ value: parentId, label: parentName || String(parentId) })
        if (catConfig) fetchSubItems(catConfig, parentId)
      }

      const rawActionTypeId = sponserData.actionTypeId
      const actionTypeIds = Array.isArray(rawActionTypeId) ? rawActionTypeId : rawActionTypeId != null ? [rawActionTypeId] : []
      if (actionTypeIds.length > 0) {
        setSelectedSubItems(actionTypeIds.map((id: any) => String(id)))
      }
    }
  }, [sponserData, categoryId])

  // The prefill above only knows the saved id, so its label falls back to that raw id
  // ("1"). Once the property list arrives, adopt the matching option itself so the real
  // name shows and react-select marks it as the selected item in the menu.
  useEffect(() => {
    if (!selectedProperty?.value || propertyList.length === 0) return
    const match = propertyList.find((p) => String(p.value) === String(selectedProperty.value))
    if (match && (match.value !== selectedProperty.value || match.label !== selectedProperty.label)) {
      setSelectedProperty(match)
    }
  }, [propertyList, selectedProperty])

  const fetchProperties = useCallback(async (catId: number, vId: number) => {
    setPropertyLoading(true)
    try {
      const response = await fetchData('admin/propertylist', { vendorId: vId, categoryId: catId, filter: false })
      if (response?.status == '200') {
        const data = Array.isArray(response?.data) ? response.data : Array.isArray(response?.data?.list) ? response.data.list : []
        const listConfig = CATEGORY_TYPES.find((c) => c.value === catId)
        setPropertyList(data.map((item: any) => ({
          // Prefer the id key this category's detail API expects, so the parent id we
          // send there (and save as actionTypeId) always belongs to this category.
          value: (listConfig && item?.[listConfig.parentIdKey])
            ?? item?.hotelId ?? item?.spacerentalId ?? item?.spaceId ?? item?.tourpackageId
            ?? item?.carrentalId ?? item?.carId ?? item?.restaurantId ?? item?.value ?? '',
          label: item?.en_name ?? item?.hotelName ?? item?.name ?? item?.label ?? '-',
        })))
      }
    } catch (error) {
      console.error('Error fetching properties:', error)
    } finally {
      setPropertyLoading(false)
    }
  }, [])

  const fetchSubItems = useCallback(async (config: typeof CATEGORY_TYPES[0], parentId: string | number) => {
    
    setSubItemLoading(true)
    try {
      // carRentalDetail / spaceDetails / tourpackageDetails expect the id as a string.
      const needsStringId = /carRentalDetail|spaceDetails|tourpackageDetails/.test(config.detailApi)
      const response = await fetchData(config.detailApi, { [config.parentIdKey]: needsStringId ? String(parentId) : parentId })
      if (response?.status == '200') {
        const detail = Array.isArray(response?.data) ? response.data[0] : response?.data
        const items = Array.isArray(detail?.[config.subKey]) ? detail[config.subKey] : []
        setSubItemList(items.map((item: any) => ({
          value: item?.[config.subIdKey] ?? item?.id ?? '',
          label: item?.[config.nameKey] ?? item?.en_name ?? '-',
        })))
        if (items.length === 0) setNoSubItems(true)
      }
    } catch (error) {
      console.error('Error fetching sub-items:', error)
    } finally {
      setSubItemLoading(false)
    }
  }, [])

  const handleVendorChange = useCallback((opt: any) => {
    setSelectedVendor(opt)
    formik.setFieldValue('vendorId', opt?.value ? String(opt.value) : '')
    formik.setFieldTouched('vendorId', true)
  }, [formik])

  const handlePropertyChange = useCallback((opt: SelectOption | null) => {
    if (opt?.value && String(opt.value) === String(selectedProperty?.value)) return
    setSelectedProperty(opt)
    setPropertyError('')
    if (isNested) {
      setSelectedSubItems([])
      setSubItemList([])
      setNoSubItems(false)
      if (opt?.value && catConfig) {
        fetchSubItems(catConfig, opt.value)
      }
    }
  }, [isNested, catConfig, fetchSubItems, selectedProperty])

  const toggleSubItem = useCallback((id: string | number) => {
    setPropertyError('')
    const strId = String(id)
    setSelectedSubItems((prev) =>
      prev.includes(strId) ? [] : [strId]
    )
  }, [])

  const getSubItemLabel = useCallback((): string => {
    if (categoryId === 5) return 'Tour Package'
    if (categoryId === 7) return 'Rentable Car'
    return 'Item'
  }, [categoryId])

  return (
    <>
      <div className='mb-6'>
        <h4 className='fw-bold fs-4 mb-1'>{t('Sponser Property Selection')}</h4>
        <p className='text-muted fs-7 mb-0'>{t('Select the property to sponsor for this vendor')}</p>
      </div>

      {/* Vendor Select */}
      <div className='mb-6'>
        <label className='sponserform-label'>{t('Vendor')} <span className='text-danger'>*</span></label>
        <Select
          placeholder={t('Search and select vendor...')}
          isDisabled={true}
          isSearchable
          options={safeVendors}
          value={selectedVendor}
          onChange={(opt: any) => handleVendorChange(opt)}
          classNamePrefix='sponserform-select'
        />
        {formik.touched.vendorId && formik.errors.vendorId && (
          <div className='text-danger fs-7 mt-2'>{formik.errors.vendorId}</div>
        )}
      </div>

      {/* Sponsored Property */}
      {categoryId > 0 && (isDirect || isNested) && (
        <div className='mb-6'>
          <div className='d-flex align-items-center gap-2 mb-3'>
            <KTIcon iconName='home-2' className='fs-2 text-primary' />
            <h5 className='fw-bold mb-0'>{t('Sponsored Property')}</h5>
          </div>
          <p className='text-muted fs-7 mb-4'>{t('Select the property to sponsor')}</p>

          <div className='mb-4' style={{ maxWidth: 400 }}>
            <label className='sponserform-label mb-2'>{t('Select Property')} <span className='text-danger'>*</span></label>
            <Select
              placeholder={propertyLoading ? t('Loading...') : t('Search and select property...')}
              isSearchable
              isLoading={propertyLoading}
              options={propertyList}
              value={selectedProperty}
              onChange={(opt: any) => handlePropertyChange(opt)}
              classNamePrefix='sponserform-select'
              noOptionsMessage={() => propertyLoading ? t('Loading...') : t('No properties found')}
            />
            {propertyError && <div className='text-danger fs-7 mt-2'>{propertyError}</div>}
          </div>

          {/* Sub-items for nested categories */}
          {isNested && selectedProperty && subItemLoading && (
            <div className='text-center py-6'>
              <span className='spinner-border spinner-border-sm text-primary me-2' />
              <span className='text-muted'>{t('Loading items...')}</span>
            </div>
          )}

          {isNested && selectedProperty && !subItemLoading && noSubItems && (
            <div className='alert alert-warning d-flex align-items-center gap-2 py-3 mt-3'>
              <KTIcon iconName='information' className='fs-2 text-warning' />
              <span>{t(`No ${getSubItemLabel().toLowerCase()}s found for this property.`)}</span>
            </div>
          )}

          {isNested && selectedProperty && !subItemLoading && subItemList.length > 0 && (
            <div className='mt-3'>
              <label className='sponserform-label mb-2'>
                {t(`Select ${getSubItemLabel()}`)} <span className='text-danger'>*</span>
              </label>
              <div className='row g-3'>
                {subItemList.map((item) => {
                  const isItemSelected = selectedSubItems.includes(String(item.value))
                  return (
                    <div className='col-6 col-md-4 col-lg-3' key={String(item.value)}>
                      <div
                        className={`sponserform-subitem-card ${isItemSelected ? 'sponserform-subitem-card--selected' : ''}`}
                        onClick={() => toggleSubItem(item.value)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className='sponserform-subitem-card__check'>
                          {isItemSelected ? (
                            <KTIcon iconName='check-circle' className='fs-2 text-primary' iconType='solid' />
                          ) : (
                            <div className='sponserform-subitem-card__unchecked' />
                          )}
                        </div>
                        <span className='sponserform-subitem-card__label'>{item.label}</span>
                      </div>
                    </div>
                  )
                })}
              </div>
              {selectedSubItems.length > 0 && (
                <div className='text-muted fs-8 mt-2'>{selectedSubItems.length} {t('selected')}</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Save & Next */}
      <div className='d-flex justify-content-end mt-6'>
        <button
          type='button'
          className='btn btn-primary d-flex align-items-center gap-2 px-5 py-3'
          onClick={() => formik.handleSubmit()}
          disabled={saving}
        >
          {saving ? <span className='spinner-border spinner-border-sm' /> : <KTIcon iconName='check' className='fs-3' />}
          <span>{t('Save & Next')}</span>
          <KTIcon iconName='arrow-right' className='fs-3' />
        </button>
      </div>
    </>
  )
}

export { PropertySelectionStep }
